import * as _ from 'lodash';
import qs from 'qs';
import { useDispatch } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import { SBOM_PORTAL_PRIVATE } from '../../../../shared/featureFlags/flagNames';
import { useReleaseGroupRelease } from '../../../hooks/api/releaseGroups/useReleaseGroupRelease';
import useOnUnmount from '../../../hooks/useOnUnmount';
import useToggle from '../../../hooks/useToggle';
import ProjectGroupModule from '../../../redux/project_grouping';
import { useSelectedReleaseId } from '../../../redux/project_grouping/hooks';
import COLORS from '../../../theme/colors';
import { fossaPrimaryFont } from '../../../theme/typography';
import Badge from '../../generic/Badge/Badge';
import Flex from '../../generic/Flex';
import FeatureFlagRequired from '../../generic/Gates/FeatureFlagRequired';
import Icon, { icons } from '../../generic/Icon';
import type { ReleaseGroupDetail } from '../../../../shared/releaseGroups';

import ReleaseGroupsReleaseSelector from './components/ReleaseSelector';

type Props = {
  projectGroupId: number;
  projectGroup?: ReleaseGroupDetail;
  location: { pathname: string; query?: Record<string, string> };
  router?: InjectedRouter;
};

/**
 * A releaseScanId in the URL belongs to whichever release was selected when it was set.
 * Returns the search string with it removed, or null if there was nothing to clear.
 */
export const clearReleaseScanIdFromSearch = (location: { query?: Record<string, string> }): string | null => {
  if (!location.query?.releaseScanId) {
    return null;
  }
  const remainingQuery = _.omit(location.query, ['releaseScanId']);
  return Object.keys(remainingQuery).length ? `?${qs.stringify(remainingQuery)}` : '';
};

const ProjectGroupNav: VoidFunctionComponent<Props> = ({
  projectGroup,
  projectGroupId,
  location,
  router,
}) => {
  const dispatch = useDispatch();
  const [isToggled, onToggle] = useToggle();
  const handleSelectRelease = (releaseId: number) => {
    if (!projectGroup) {
      return;
    }
    //  Switching releases makes any releaseScanId already in the URL stale, since it belongs
    //  to the release we're switching away from. Clear it so the new release's scope isn't
    //  computed with a scan id from the old one.
    const search = clearReleaseScanIdFromSearch(location);
    if (search !== null && router) {
      router.replace({ pathname: location.pathname, search });
    }
    dispatch(ProjectGroupModule.actions.selectRelease(projectGroup.id, releaseId));
    onToggle(); // Close the switcher
  };
  const currentReleaseId = useSelectedReleaseId(projectGroupId);
  const { data: selectedRelease } = useReleaseGroupRelease(projectGroupId, currentReleaseId);
  const currentReleaseTitle = selectedRelease?.title;

  //  We hide the releases selector if we are on the releases or settings tabs.
  const isReleaseTab = location?.pathname?.endsWith('releases');
  const isSettingsTab = location?.pathname?.includes('settings/');
  const shouldHideReleaseSelector = isReleaseTab || isSettingsTab;

  //  Ensure that the dropdown is closed when the component unmounts.
  useOnUnmount(() => {
    if (isToggled) {
      onToggle();
    }
  });

  return (
    <div>
      <div style={{
        textTransform: 'uppercase',
        color: 'rgba(231, 236, 243, 0.6)',
        fontWeight: '600',
        fontSize: '12px',
        marginBottom: '4px',
      }}>
        Release Group
      </div>
      <div style={{ marginBottom: '34px' }}>
        <Flex justify="flex-start">
          <h1 style={{ margin: 0, display: 'flex' }}>
            <span className="margin-right-sm" style={{ fontSize: '24px', color: '#90A0B6', marginTop: '4px' }}>
              <Icon icon={icons.folderOpenO} />
            </span>
            {projectGroup?.title || <em>Loading...</em>}
          </h1>
          {projectGroup && !shouldHideReleaseSelector && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span className="margin-left-sm margin-right-sm">
                <Icon icon={icons.angleRight} />
              </span>
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    color: COLORS.fossaGreen,
                    fontSize: '24px',
                    fontFamily: fossaPrimaryFont,
                    padding: '12px 0',
                    cursor: 'pointer',
                  }}
                  onClick={onToggle}
                >
                  {currentReleaseTitle}
                  <span className="margin-left-sm" style={{ fontSize: '12px' }}>
                    <Icon icon={icons.caretDown} />
                  </span>
                </div>
                {isToggled && (
                  <ReleaseGroupsReleaseSelector
                    projectGroupId={projectGroupId}
                    selectedReleaseId={currentReleaseId ?? undefined}
                    onReleaseSelect={handleSelectRelease}
                    onClose={onToggle}
                  />
                )}
              </div>
              {
                !projectGroup.publicOnPortal &&
                <FeatureFlagRequired flagName={SBOM_PORTAL_PRIVATE}>
                  <div className="margin-left-sm">
                    <Badge color="dark-gray" shape="circle">
                      <Icon icon={icons.lockOutline} /> Private
                    </Badge>
                  </div>
                </FeatureFlagRequired>
              }
            </div>
          )}
        </Flex>
      </div>
    </div>
  );
};

export default ProjectGroupNav;
